import React, { useEffect, useMemo, useState } from 'react';
import { CreativeWithExternalId } from '../../services/measurementService';
import AddCreativeDialog from './AddCreativeDialog';
import './MeasurementForm.css';

interface PendingCreativesFieldProps {
  creatives: CreativeWithExternalId[];
  onAdd: (creative: CreativeWithExternalId) => void | Promise<void>;
  onRemove: (index: number) => void;
  disabled?: boolean;
}

export function truncateDisplayUrl(url: string, maxLength = 42): string {
  let display = url;
  try {
    const parsed = new URL(url);
    display = parsed.host + parsed.pathname + parsed.search;
    if (display.endsWith('/')) {
      display = display.slice(0, -1);
    }
  } catch {
    display = url;
  }

  if (display.length <= maxLength) {
    return display;
  }

  return `${display.slice(0, maxLength - 1)}…`;
}

export function creativeSourceLabel(creative: CreativeWithExternalId): string {
  if (creative.file) {
    return creative.file.name;
  }
  return creative.previewUrl ? truncateDisplayUrl(creative.previewUrl) : '';
}

function FileThumb({ file }: { file: File }) {
  const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(objectUrl), [objectUrl]);

  if (file.type.startsWith('image/')) {
    return <img src={objectUrl} alt="" className="preview-image" />;
  }
  if (file.type.startsWith('video/')) {
    return <video src={objectUrl} className="preview-video" muted />;
  }
  return <div className="pending-creative-placeholder">FILE</div>;
}

const PendingCreativesField: React.FC<PendingCreativesFieldProps> = ({
  creatives,
  onAdd,
  onRemove,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="form-group">
      <label>Creatives</label>
      <button
        type="button"
        className="add-creative-open-button"
        onClick={() => setDialogOpen(true)}
        disabled={disabled}
      >
        Add creative
      </button>

      {creatives.length > 0 && (
        <div className="creatives-preview">
          <h3>Selected creatives ({creatives.length})</h3>
          <div className="pending-creatives-grid">
            {creatives.map((creative, index) => (
              <div key={`${creative.externalId}-${index}`} className="pending-creative-card">
                <div className="creative-preview">
                  {creative.file ? (
                    <FileThumb file={creative.file} />
                  ) : (
                    <div
                      className="pending-creative-placeholder"
                      title={creative.previewUrl}
                    >
                      URL
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="remove-button pending-creative-remove"
                    disabled={disabled}
                    aria-label={`Remove ${creativeSourceLabel(creative)}`}
                  >
                    ×
                  </button>
                </div>
                <div className="pending-creative-info">
                  <span
                    className="file-name"
                    title={creative.file?.name ?? creative.previewUrl}
                  >
                    {creativeSourceLabel(creative)}
                  </span>
                  <span className="file-size">{creative.externalId}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddCreativeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={onAdd}
        disabled={disabled}
      />
    </div>
  );
};

export default PendingCreativesField;
